import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import ShareRoundedIcon from '@mui/icons-material/ShareRounded'
import ShareLinksDialog from '../common/ShareLinksDialog'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded'
import HighlightDecorator from '../highlight/HighlightDecorator'
import LinkPopover from '../common/LinkPopover'
import LinkNotice, { copyWithNotice, useLinkNotice } from '../common/LinkNotice'
import env from '../../config/env'
import {
  PREVIEW_LINK_COPIED,
  PREVIEW_LINK_FAILED,
  PREVIEW_LINK_LABEL,
  PREVIEW_LINK_NOT_COPIED,
  PREVIEW_LINK_NOTICE_MS,
  PREVIEW_LINK_TOOLTIP,
  SIGNED_IN_LINK_NOT_COPIED,
  WATCH_LINKS_LABEL
} from '../../constants/sharing'
import { useAuth } from '../../auth/AuthContext'
import { createPublicLink } from '../../service'
import { clipRange } from '../video/uploadTimeline'

HighlightCard.propTypes = {
  value: PropTypes.object,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onRefresh: PropTypes.func,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  isNext: PropTypes.bool,
  isPrev: PropTypes.bool,
  embedded: PropTypes.bool,
  canShareWatchLink: PropTypes.bool,
  lockedReason: PropTypes.string
}

// 36px targets, 44px on touch screens.
const iconButtonSx = { width: 36, height: 36, '@media (pointer: coarse)': { width: 44, height: 44 } }

const InspectorCollection = ({ label, values, urlFn, initialLimit = 6 }) => {
  const [expanded, setExpanded] = React.useState(false)
  const items = values || []
  if (!items.length) return null

  const visible = expanded ? items : items.slice(0, initialLimit)
  const hiddenCount = Math.max(0, items.length - visible.length)

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 0.65 }}
      >
        <Typography
          color="text.secondary"
          className="editor-kicker"
          sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '.08em' }}
        >
          {label}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13 }}>
          {items.length}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {visible.map(item => {
          const name = item.name || item.raw_value || ''
          return (
            <Chip
              key={item.id || name}
              label={name}
              component={urlFn ? 'a' : 'div'}
              href={urlFn ? urlFn(item) : undefined}
              clickable={Boolean(urlFn)}
              size="small"
              variant="outlined"
              sx={{
                height: 26,
                maxWidth: '100%',
                '& .MuiChip-label': {
                  px: 0.9,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            />
          )
        })}

        {hiddenCount > 0
          ? <Chip
              label={`+${hiddenCount}`}
              size="small"
              variant="outlined"
              onClick={() => setExpanded(true)}
              sx={{ height: 26, cursor: 'pointer' }}
            />
          : null}

        {expanded && items.length > initialLimit
          ? <Button
              size="small"
              color="inherit"
              onClick={() => setExpanded(false)}
              sx={{ minWidth: 0, px: 0.75, fontSize: 13 }}
            >
              Show less
            </Button>
          : null}
      </Stack>
    </Box>
  )
}

InspectorCollection.propTypes = {
  label: PropTypes.string.isRequired,
  values: PropTypes.array,
  urlFn: PropTypes.func,
  initialLimit: PropTypes.number
}

export default function HighlightCard ({
  value,
  onEdit,
  onDelete,
  onRefresh,
  onPrev,
  onNext,
  isNext,
  isPrev,
  embedded = false,
  canShareWatchLink = false,
  // Why the highlight can't be edited, deleted or shared here (a part combined into a full game video), or null.
  lockedReason = null
}) {
  const { user } = useAuth()
  const { anchor: popoverEl, notice, show, close } = useLinkNotice()
  const [linksOpen, setLinksOpen] = React.useState(false)
  const canDelete = Boolean(user && user.username === value.owner)

  const linkClicked = ev => copyWithNotice({
    url: `${env.publicLinkUrl}/upload/${value.uploadHighlightsId}/highlight/${value.id}`,
    anchor: ev.currentTarget,
    show,
    copied: 'Highlight link copied. Login is still required.',
    notCopied: SIGNED_IN_LINK_NOT_COPIED,
    durationMs: 2000
  })

  const publicLinkClicked = async ev => {
    // React clears ev.currentTarget once the handler yields, so capture it before awaiting.
    const anchor = ev.currentTarget
    let link
    try {
      link = await createPublicLink({
        uploadId: value.uploadHighlightsId,
        highlightId: value.id
      })
    } catch (err) {
      console.error(err)
      // 409: this part was combined into a full game video meanwhile; the API says so (D34).
      const message = err?.status === 409 && err.detail ? err.detail : PREVIEW_LINK_FAILED
      show(anchor, { message, severity: 'error' }, { durationMs: PREVIEW_LINK_NOTICE_MS })
      return
    }
    // The link now exists. If copying fails, show it rather than inviting a retry
    // that would create another one.
    await copyWithNotice({
      url: `${env.publicLinkUrl}${link.path}`,
      anchor,
      show,
      copied: PREVIEW_LINK_COPIED,
      notCopied: PREVIEW_LINK_NOT_COPIED,
      durationMs: PREVIEW_LINK_NOTICE_MS
    })
  }

  return (
    <Paper
      variant="outlined"
      className={embedded ? undefined : 'editor-inspector-panel'}
      sx={{
        overflow: 'hidden',
        border: embedded ? 0 : undefined,
        borderRadius: embedded ? 0 : undefined,
        background: embedded ? 'transparent' : undefined,
        boxShadow: embedded ? 'none' : undefined
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            {!embedded
              ? <Typography
                  variant="caption"
                  color="primary.light"
                  className="editor-kicker"
                  sx={{ fontSize: 13, fontWeight: 750 }}
                >
                  Current clip
                </Typography>
              : null}
            <Typography
              variant="h6"
              sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere' }}
            >
              <HighlightDecorator value={value} /> {value.name}
            </Typography>
            {value.playerLinked && <Chip size="small" label={value.playerName ? `Player · ${value.playerName}` : 'Player linked'} sx={{ mt: 0.75 }} />}
            {value.origin === 'ai' && <Chip size="small" color="warning" variant="outlined" label="AI · beta" title="Saved from an AI analysis. A newer run can update this clip until you edit it." sx={{ mt: 0.75, ml: value.playerLinked ? 0.5 : 0 }} />}
          </Box>

          <Stack direction="row" spacing={0.25} flexShrink={0}>
            {/* describeChild keeps the name on the button rather than the wrapper the tooltip needs when it is disabled. */}
            <Tooltip title="Previous highlight" describeChild>
              <span>
                <IconButton sx={iconButtonSx} aria-label="Previous highlight" onClick={onPrev} disabled={!isPrev}>
                  <NavigateBeforeRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next highlight" describeChild>
              <span>
                <IconButton sx={iconButtonSx} aria-label="Next highlight" onClick={onNext} disabled={!isNext}>
                  <NavigateNextRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Divider />

      <Stack spacing={1.15} sx={{ px: 1.5, py: 1.15 }}>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip title={lockedReason || 'Edit highlight'} describeChild>
            <span>
              <IconButton sx={iconButtonSx} aria-label="Edit highlight" onClick={onEdit} disabled={Boolean(lockedReason)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Replay highlight">
            <IconButton sx={iconButtonSx} aria-label="Replay highlight" onClick={onRefresh}>
              <ReplayRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy authenticated highlight link">
            <IconButton sx={iconButtonSx} aria-label="Copy authenticated highlight link" onClick={linkClicked}>
              <LinkRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={lockedReason || PREVIEW_LINK_TOOLTIP} describeChild>
            <span>
              <IconButton sx={iconButtonSx} aria-label={PREVIEW_LINK_LABEL} onClick={publicLinkClicked} disabled={Boolean(lockedReason)}>
                <PublicRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {canShareWatchLink
            ? (
              <Tooltip title={WATCH_LINKS_LABEL}>
                <IconButton sx={iconButtonSx} aria-label="Shared links" onClick={() => setLinksOpen(true)}>
                  <ShareRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              )
            : null}
          <Box sx={{ flex: 1 }} />
          <Tooltip title={lockedReason || 'Delete highlight'} describeChild>
            <span>
              <IconButton
                sx={iconButtonSx}
                aria-label="Delete highlight"
                color="error"
                onClick={onDelete}
                disabled={!canDelete || Boolean(lockedReason)}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Divider />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '60px minmax(0, 1fr)',
            alignItems: 'baseline',
            gap: '6px 10px',
            '& > .label': { color: 'text.secondary', fontSize: 13, lineHeight: '20px' },
            '& > .value': { fontSize: 14, lineHeight: '20px', minWidth: 0 }
          }}
        >
          <Typography className="label">Range</Typography>
          <Typography className="value" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {clipRange(value.start, value.end)}
          </Typography>

          {value.owner
            ? <>
                <Typography className="label">Author</Typography>
                <Typography className="value" noWrap>{value.owner}</Typography>
              </>
            : null}

          <Typography className="label">Access</Typography>
          <Typography className="value" sx={{ overflowWrap: 'anywhere' }}>
            {value.groups && value.groups.length ? value.groups.join(', ') : 'Private'}
          </Typography>
        </Box>

        {value.description
          ? <>
              <Divider />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.45,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {value.description}
              </Typography>
            </>
          : null}

        {value.tags && value.tags.items && value.tags.items.length
          ? <>
              <Divider />
              <InspectorCollection
                label="Tags"
                values={value.tags.items}
                urlFn={item => `/tag/${item.name}`}
                initialLimit={6}
              />
            </>
          : null}

        {value.stats && value.stats.items && value.stats.items.length
          ? <>
              <Divider />
              <InspectorCollection
                label="Stats"
                values={value.stats.items}
                initialLimit={5}
              />
            </>
          : null}
      </Stack>

      <LinkPopover el={popoverEl} setEl={close} horizontal="left">
        <LinkNotice notice={notice} />
      </LinkPopover>
      {canShareWatchLink
        ? <ShareLinksDialog open={linksOpen} onClose={() => setLinksOpen(false)} uploadId={value.uploadHighlightsId} highlightId={value.id} title={value.name} lockedReason={lockedReason} />
        : null}
    </Paper>
  )
}
